import { Controller } from "@hotwired/stimulus"
import { trackEvent } from "../analytics"

// Reports its `event` analytics event the first time the attached beat scrolls
// into the viewport. Used on the advocate site to measure whether the advocate
// actually saw a beat (e.g. the waiver-signed notification), beyond just opening
// the conversation. The event name is supplied per beat type by the component.
//
// Example usage:
//   <div data-controller="beat-visibility"
//        data-beat-visibility-event-value="waiver.signed_seen"
//        data-beat-visibility-beat-type-value="Community::Conversation::Beat::WaiverSigned"
//        data-beat-visibility-beat-ulid-value="01J..."
//        data-beat-visibility-conversation-id-value="123"></div>
export default class extends Controller {
  static values = { event: String, beatType: String, beatUlid: String, conversationId: Number }

  connect() {
    this.reported = false
    this.observer = new IntersectionObserver(
      (entries) => this.onIntersect(entries),
      { threshold: 0.5 },
    )
    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer?.disconnect()
  }

  onIntersect(entries) {
    if (this.reported) return
    if (!entries.some((entry) => entry.isIntersecting)) return

    this.reported = true
    this.observer.disconnect()

    // Dedupe across Turbo navigations / back-forward cache within the tab —
    // connect() re-runs on restore, so guard on session storage too.
    if (this.alreadyReported()) return
    this.markReported()
    this.report()
  }

  storageKey() {
    return `beat-seen:${this.conversationIdValue}:${this.beatUlidValue}`
  }

  alreadyReported() {
    try {
      return sessionStorage.getItem(this.storageKey()) === "1"
    } catch {
      return false
    }
  }

  markReported() {
    try {
      sessionStorage.setItem(this.storageKey(), "1")
    } catch {
      // sessionStorage unavailable (private mode quota) — fall back to the
      // per-connect this.reported guard.
    }
  }

  report() {
    trackEvent(this.eventValue, {
      beat_type: this.beatTypeValue,
      beat_ulid: this.beatUlidValue,
      conversation_id: this.conversationIdValue,
    })
  }
}
